import { GripVertical } from 'lucide-react'
import { type KeyboardEvent, type PointerEvent, type RefObject } from 'react'

import { CART_MIN, PRODUCTS_MIN } from '@/hooks/ui/useCartWidth'

const STEP = 16

interface CartResizeHandleProps {
  /** The Sell page grid, measured for the limits. */
  container: RefObject<HTMLElement | null>
  /** The cart column, measured when no width is set yet. */
  cart: RefObject<HTMLElement | null>
  width: number | null
  onChange: (width: number) => void
  onCommit: (width: number | null) => void
}

/** Drag bar on the cart's left edge: drag to resize, arrow keys to nudge, double-click to reset. */
export const CartResizeHandle = ({ container, cart, width, onChange, onCommit }: CartResizeHandleProps) => {
  const clamp = (w: number) => {
    const total = container.current?.clientWidth ?? 0
    return Math.round(Math.min(Math.max(w, CART_MIN), Math.max(CART_MIN, total - PRODUCTS_MIN)))
  }
  const current = () => width ?? cart.current?.clientWidth ?? CART_MIN

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || !container.current) return
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    const right = container.current.getBoundingClientRect().right
    let last = current()
    const move = (ev: globalThis.PointerEvent) => {
      last = clamp(right - ev.clientX)
      onChange(last)
    }
    const up = () => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', up)
      handle.removeEventListener('pointercancel', up)
      onCommit(last)
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', up)
    handle.addEventListener('pointercancel', up)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    onCommit(clamp(current() + (e.key === 'ArrowLeft' ? STEP : -STEP)))
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the cart"
      aria-valuenow={width ?? undefined}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => onCommit(null)}
      className="group absolute inset-y-0 -left-1.5 z-10 hidden w-3 cursor-col-resize touch-none outline-none lg:block"
    >
      <span className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 transition-colors group-hover:bg-primary/40 group-focus-visible:bg-primary group-active:bg-primary" />
      <span className="absolute left-1/2 top-1/2 grid h-8 w-4 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-sm border border-border bg-card text-muted-foreground shadow-card transition-colors group-hover:border-primary/40 group-hover:text-ink group-focus-visible:border-primary group-active:border-primary">
        <GripVertical className="size-3.5" />
      </span>
    </div>
  )
}
